import React from "react";
import { Eye, MapPin, Languages, Clock, BadgeCheck, Star } from "lucide-react";
import { SectionTitle } from "../primitives";
import { DURATION_KEYS, SERVICE_CATEGORIES } from "../constants";
import { resolveImageUrl as resolveUrl, onImageError } from "@/lib/img";

/**
 * Live profile preview — emulates the public profile page so users see
 * exactly how their profile will appear before saving.
 */
export default function Step10Preview({ form }) {
  const photos = form.gallery.filter(g => g.type === "image");
  const main = form.gallery.find(g => g.is_main && g.type === "image") || photos[0];
  const cover = form.cover_photo || main?.url;
  const enabledServices = Object.entries(form.services_matrix || {})
    .filter(([, v]) => v?.status && v.status !== "not_available");

  return (
    <div className="space-y-6">
      <SectionTitle icon={Eye} title="Profile preview"
        subtitle="This is exactly how clients will see your profile." />

      <div className="rounded-2xl overflow-hidden border border-stone-800 bg-stone-950/60">
        {/* Cover */}
        <div className="relative aspect-[16/9] sm:aspect-[5/2] bg-stone-900">
          {cover && <img src={resolveUrl(cover)} onError={onImageError} alt="cover" className="w-full h-full object-cover opacity-90" />}
          <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 flex items-end gap-4">
            {main && (
              <img
                src={resolveUrl(main.url)} onError={onImageError} alt=""
                className="w-20 h-24 sm:w-28 sm:h-36 object-cover rounded-lg border-2 border-amber-500/60 shadow-xl"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-xl sm:text-2xl font-serif text-stone-50 truncate">
                  {form.display_name || "Your name"}
                  {form.age ? <span className="text-stone-400 font-light ml-2">{form.age}</span> : null}
                </h2>
                <BadgeCheck className="w-5 h-5 text-amber-400 flex-none" />
              </div>
              <p className="text-xs sm:text-sm text-stone-300 mt-1 line-clamp-2">{form.headline}</p>
              <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] sm:text-xs text-stone-400">
                <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {form.city || "—"}{form.area ? ` · ${form.area}` : ""}</span>
                {form.languages?.length > 0 && (
                  <span className="inline-flex items-center gap-1"><Languages className="w-3 h-3" /> {form.languages.slice(0, 3).join(", ")}</span>
                )}
                {form.available_now && <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300">Available now</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 p-4 sm:p-6">
          {/* Bio + interests */}
          <div className="lg:col-span-2 space-y-5">
            {form.bio && (
              <section>
                <h3 className="text-xs uppercase tracking-[0.2em] text-amber-300 mb-2">About</h3>
                <p className="text-sm text-stone-300 whitespace-pre-line">{form.bio}</p>
              </section>
            )}
            {form.personality?.length > 0 && (
              <section>
                <h3 className="text-xs uppercase tracking-[0.2em] text-amber-300 mb-2">Personality</h3>
                <div className="flex flex-wrap gap-1.5">
                  {form.personality.map(t => <span key={t} className="px-2 py-1 rounded-full bg-stone-900 border border-stone-700 text-xs text-stone-200">{t}</span>)}
                </div>
              </section>
            )}
            {form.interests?.length > 0 && (
              <section>
                <h3 className="text-xs uppercase tracking-[0.2em] text-amber-300 mb-2">Interests</h3>
                <div className="flex flex-wrap gap-1.5">
                  {form.interests.map(t => <span key={t} className="px-2 py-1 rounded-full bg-stone-900 border border-stone-700 text-xs text-stone-200">{t}</span>)}
                </div>
              </section>
            )}

            {enabledServices.length > 0 && (
              <section>
                <h3 className="text-xs uppercase tracking-[0.2em] text-amber-300 mb-2">Services</h3>
                <div className="rounded-lg border border-stone-800 divide-y divide-stone-800/80 bg-stone-900/40">
                  {SERVICE_CATEGORIES.flatMap(c => c.items)
                    .filter(s => form.services_matrix?.[s.k]?.status && form.services_matrix[s.k].status !== "not_available")
                    .map(s => {
                      const e = form.services_matrix[s.k];
                      return (
                        <div key={s.k} className="px-3 py-2 flex items-center justify-between text-sm">
                          <span className="text-stone-200">{s.label}</span>
                          <span className={`text-xs ${e.status === "included" ? "text-emerald-300" : e.status === "extra" ? "text-amber-300" : "text-stone-400"}`}>
                            {e.status === "extra"
                              ? `+ ${form.currency} ${e.price || "—"}`
                              : e.status === "included" ? "Included" : "Available"}
                          </span>
                        </div>
                      );
                    })}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar — pricing & quick facts */}
          <aside className="space-y-4">
            <div className="rounded-xl border border-stone-800 bg-stone-900/40 p-4">
              <h4 className="text-xs uppercase tracking-[0.2em] text-amber-300 mb-2">Rates</h4>
              <ul className="space-y-1.5 text-sm">
                {DURATION_KEYS.filter(d => form.pricing?.[d.k]).map(d => (
                  <li key={d.k} className="flex items-center justify-between text-stone-200">
                    <span>{d.label}</span>
                    <span className="font-semibold text-amber-300">{form.currency} {form.pricing[d.k]}</span>
                  </li>
                ))}
                {form.pricing?.custom?.filter(c => c.label && c.price).map((c) => (
                  <li key={`cp-${c.label}-${c.price}`} className="flex items-center justify-between text-stone-200">
                    <span>{c.label}</span>
                    <span className="font-semibold text-amber-300">{form.currency} {c.price}</span>
                  </li>
                ))}
              </ul>
              {form.pricing_modes?.length > 0 && (
                <div className="mt-3 pt-3 border-t border-stone-800 flex flex-wrap gap-1.5">
                  {form.pricing_modes.map(m => <span key={m} className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 uppercase tracking-wider">{m}</span>)}
                </div>
              )}
            </div>

            <div className="rounded-xl border border-stone-800 bg-stone-900/40 p-4 text-sm space-y-2">
              <h4 className="text-xs uppercase tracking-[0.2em] text-amber-300 mb-1">Quick facts</h4>
              {form.height && <Row label="Height" v={`${form.height} cm`} />}
              {form.weight && <Row label="Weight" v={`${form.weight} kg`} />}
              {form.body_type && <Row label="Body" v={form.body_type} />}
              {form.hair_color && <Row label="Hair" v={form.hair_color} />}
              {form.eye_color && <Row label="Eyes" v={form.eye_color} />}
              {form.bust_size && <Row label="Bust" v={form.bust_size} />}
              {form.dress_size && <Row label="Dress" v={form.dress_size} />}
              {form.smoking && <Row label="Smoking" v={form.smoking} />}
              {form.drinking && <Row label="Drinking" v={form.drinking} />}
              {form.travel_available && <Row label="Travels" v="Yes" />}
            </div>

            <div className="rounded-xl border border-stone-800 bg-stone-900/40 p-4 text-sm">
              <h4 className="text-xs uppercase tracking-[0.2em] text-amber-300 mb-2 inline-flex items-center gap-1.5"><Clock className="w-3 h-3" /> Hours</h4>
              {form.is_24_7 ? <p className="text-stone-300">Available 24 / 7</p>
                : form.vacation_mode ? <p className="text-rose-300">On vacation</p>
                : (
                  <ul className="space-y-0.5 text-xs text-stone-300">
                    {Object.entries(form.working_hours || {}).map(([d, cfg]) => (
                      <li key={d} className="flex justify-between">
                        <span className="uppercase text-stone-500">{d}</span>
                        <span>{cfg.closed ? "Closed" : `${cfg.open}–${cfg.close}`}</span>
                      </li>
                    ))}
                  </ul>
                )}
            </div>
          </aside>
        </div>

        {/* Mini gallery strip */}
        {photos.length > 1 && (
          <div className="px-4 sm:px-6 pb-6">
            <h3 className="text-xs uppercase tracking-[0.2em] text-amber-300 mb-2">Gallery ({photos.length})</h3>
            <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 snap-x snap-mandatory">
              {photos.map((p, i) => (
                <img key={p.url || `photo-${i}`} src={resolveUrl(p.url)} onError={onImageError} alt="" className="h-32 w-24 sm:h-40 sm:w-32 object-cover rounded-lg flex-none border border-stone-800 snap-start" />
              ))}
            </div>
          </div>
        )}
      </div>

      <p className="text-[11px] text-stone-500 flex items-center gap-1 justify-center">
        <Star className="w-3 h-3 text-amber-400" /> Click <span className="text-amber-300 font-semibold">“Publish profile”</span> below to save and make it live.
      </p>
    </div>
  );
}

function Row({ label, v }) {
  return (
    <div className="flex items-center justify-between text-stone-300">
      <span className="text-stone-500 text-xs">{label}</span>
      <span>{v}</span>
    </div>
  );
}
